import React, { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { green, grey } from '@mui/material/colors';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { usePdf } from 'hooks/usePdf';
import {
  DealerActivitySubmissionsReportQueryResponse,
  LenderRetailActivityDealerRowViewModel,
} from '../../../api/types';
import { lenderActivityDashboardApi } from '../../../api';
import {
  DEALER_ACTIVITY_COLUMNS,
  exportLenderSubmissionsToCsv,
  getCountryLabel,
  getDealerActivityTotalRow,
  groupDealerActivityRowsByCountry,
} from '../../../lib';
import { LenderRetailActivityReportInput } from '../../../types';
import { getCountryFlag } from './country';

interface Props {
  rows: LenderRetailActivityDealerRowViewModel[];
  input: LenderRetailActivityReportInput;
  totalCreditApps: number;
  totalUnits: number;
  loading?: boolean;
}

const cellSx = { border: '1px solid', borderColor: 'divider', py: 1, px: 1.5 } as const;
const headerCellSx = { ...cellSx, bgcolor: grey[300], fontWeight: 700 } as const;
const totalCellSx = { ...cellSx, bgcolor: green[50], color: green[800], fontWeight: 600 } as const;

export const DealerActivityTable = ({ rows, input, totalCreditApps, totalUnits, loading }: Props) => {
  const groups = groupDealerActivityRowsByCountry(rows);
  const totalRow = getDealerActivityTotalRow(totalCreditApps, totalUnits);
  const colSpan = DEALER_ACTIVITY_COLUMNS.length;
  const { contentRef, handleDownloadPdf } = usePdf();

  const [fetchSubmissionsReport, { loading: isExportingCsv }] =
    useLazyQuery<DealerActivitySubmissionsReportQueryResponse>(lenderActivityDashboardApi.getDealerSubmissionsReport, {
      context: { authRequired: true },
    });

  const handleExportCsv = useCallback(async () => {
    const { data } = await fetchSubmissionsReport({ variables: { input } });
    const submissions = data?.lenderRetailActivity?.dealerSubmissionsReport?.entity ?? [];
    exportLenderSubmissionsToCsv(submissions, 'dealer-submissions-report.csv');
  }, [fetchSubmissionsReport, input]);

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" component="span">
          Dealer Activity
        </Typography>

        <Stack direction="row" gap={1} data-pdf-exclude>
          <Button size="small" variant="outlined" startIcon={<CloudDownloadIcon />} onClick={() => handleDownloadPdf()}>
            Export PDF
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={handleExportCsv}
            disabled={isExportingCsv}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <Box ref={contentRef}>
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {DEALER_ACTIVITY_COLUMNS.map(column => (
                  <TableCell key={column.key} align={column.align} sx={headerCellSx}>
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && !groups.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} sx={cellSx}>
                    <Skeleton height={160} />
                  </TableCell>
                </TableRow>
              ) : !groups.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={cellSx}>
                    <Typography>No data</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {groups.map(group => {
                    const flag = getCountryFlag(group.country);

                    return (
                      <React.Fragment key={group.country}>
                        <TableRow>
                          <TableCell colSpan={colSpan} sx={{ ...cellSx, bgcolor: grey[50] }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                              {flag && <Box component={flag} sx={{ width: 18, height: 18 }} />}
                              <Typography variant="subtitle2" component="span">
                                {getCountryLabel(group.country)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {group.rows.map(row => (
                          <TableRow key={row.id}>
                            {DEALER_ACTIVITY_COLUMNS.map(column => (
                              <TableCell key={column.key} align={column.align} sx={cellSx}>
                                {column.render(row)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {totalRow && groups.length > 0 && (
                    <TableRow>
                      {DEALER_ACTIVITY_COLUMNS.map(column => (
                        <TableCell key={column.key} align={column.align} sx={totalCellSx}>
                          {column.render(totalRow)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
};
